import { combineEpics, ofType } from 'redux-observable';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

import { rs } from 'core/api';
import { reduxSwitch } from 'core/redux/tools';
import { internalizePage, timestamps } from 'core/redux/serializers';
import { ActionGeneratorBuilder, errorMap } from 'core/redux/actions';
import { actions as leasesActions } from './leases';
import { actions as officersActions } from './officers';

const defaultPageSize = 10;

const internalize = timestamps(['createdAt', 'sentAt', 'deliveredAt']).internalize;

export const actions = new ActionGeneratorBuilder('veta-tep-notifications')
	.subtype('loadClientsNotifications', (loadClientsNotifications) =>
		loadClientsNotifications.request('payload').success({ page: true }).fail(),
	)
	.subtype('loadOfficersNotifications', (loadClientNotifications) =>
		loadClientNotifications.request('payload').success({ page: true }).fail(),
	)
	.subtype('notifyOfficers', (loadClientNotifications) => loadClientNotifications.request('payload').success().fail())
	.type('cancel')
	.build();

const defaultState = {
	clients: null,
	officers: null,
	request: { isLoading: false, error: null },
};

const notificationsReducer = (state, action) => {
	switch (action.type) {
		case actions.loadClientsNotifications.request.type:
		case actions.loadOfficersNotifications.request.type:
		case actions.notifyOfficers.request.type:
			return { ...state, request: { isLoading: true, error: null } };
		case actions.loadClientsNotifications.success.type:
			return { ...state, clients: action.page, request: { isLoading: false, error: null } };
		case actions.loadOfficersNotifications.success.type:
			return { ...state, officers: action.page, request: { isLoading: false, error: null } };
		case actions.notifyOfficers.success.type:
			return { ...state, request: { isLoading: false, error: null } };
		case actions.loadClientsNotifications.fail.type:
		case actions.loadOfficersNotifications.fail.type:
			return { ...state, request: { isLoading: false, error: action.errorMessage } };
		case actions.notifyOfficers.fail.type:
			return {
				...state,
				request: {
					isLoading: false,
					error: action.errorMessage?.toLowerCase()?.includes?.('constraint violation')
						? 'unable to notify due to an error'
						: action.errorMessage,
				},
			};
		case actions.cancel.type:
			return { ...state, request: { isLoading: false, error: null } };
		default:
			return state;
	}
};

export const reducer = reduxSwitch([notificationsReducer], defaultState);

const loadClientsNotificationsEpic = (action$) => {
	return action$.pipe(
		ofType(actions.loadClientsNotifications.request.type),
		switchMap((action) =>
			rs('veta/entry-passes/notifications/client/page')
				.optional('no', action.payload.no)
				.optional('sort', action.payload.sort)
				.optional('search', action.payload.search)
				.param('size', action.payload.size | defaultPageSize)
				.defer$()
				.pipe(
					map(internalizePage(action.payload.size | defaultPageSize, internalize)),
					map((page) => actions.loadClientsNotifications.success({ page })),
					errorMap(actions.loadClientsNotifications.fail),
				),
		),
	);
};

const loadOfficersNotificationsEpic = (action$) => {
	return action$.pipe(
		ofType(actions.loadOfficersNotifications.request.type),
		switchMap((action) =>
			rs('veta/entry-passes/notifications/officer/page')
				.optional('no', action.payload.no)
				.optional('sort', action.payload.sort)
				.optional('search', action.payload.search)
				.param('size', action.payload.size | defaultPageSize)
				.defer$()
				.pipe(
					map(internalizePage(action.payload.size | defaultPageSize, internalize)),
					map((page) => actions.loadOfficersNotifications.success({ page })),
					errorMap(actions.loadOfficersNotifications.fail),
				),
		),
	);
};

const notifyOfficersEpic = (action$) => {
	return action$.pipe(
		ofType(actions.notifyOfficers.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/notifications/officer/notify/batch`)
				.post(action.payload)
				.defer$()
				.pipe(
					mergeMap(() => of(leasesActions.close(), officersActions.cancel(), actions.notifyOfficers.success())),
					errorMap(actions.notifyOfficers.fail),
				),
		),
	);
};

export const epic = combineEpics(loadClientsNotificationsEpic, loadOfficersNotificationsEpic, notifyOfficersEpic);
